import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { MapPin, DollarSign, ArrowRight, Loader2 } from "lucide-react"
import { useLanguage } from "@/lib/i18n"
import { apiClient } from "@/lib/api"
import type { Vacancy } from "@shared/types"

interface VacanciesSectionProps {
  onApply?: (vacancyId: number) => void
}

export default function VacanciesSection({ onApply }: VacanciesSectionProps) {
  const { t, language } = useLanguage()
  const [vacancies, setVacancies] = useState<Vacancy[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadVacancies()
  }, [])

  const loadVacancies = async () => {
    try {
      const response = await apiClient.vacancies.list()
      setVacancies(response.data as Vacancy[])
    } catch (error) {
      console.error("Failed to load vacancies:", error)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = (v: Vacancy) =>
    language === "es" && v.titleEs ? v.titleEs : v.title

  const getDescription = (v: Vacancy) =>
    language === "es" && v.descriptionEs ? v.descriptionEs : v.description

  const getRequirements = (v: Vacancy) =>
    language === "es" && v.requirementsEs ? v.requirementsEs : v.requirements

  const handleApply = (vacancyId: number) => {
    if (onApply) {
      onApply(vacancyId)
    }
    const applySection = document.getElementById("apply")
    if (applySection) {
      applySection.scrollIntoView({ behavior: "smooth" })
    }
  }

  if (loading) {
    return (
      <section id="vacancies" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </section>
    )
  }

  if (vacancies.length === 0) {
    return (
      <section id="vacancies" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary font-semibold tracking-wider uppercase mb-4">
              {t("vacancies.tagline")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">
              {t("vacancies.title")}
            </h2>
            <p className="text-muted-foreground text-lg">
              {t("vacancies.empty")}
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="vacancies" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-primary font-semibold tracking-wider uppercase mb-4">
            {t("vacancies.tagline")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">
            {t("vacancies.title")}
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {t("vacancies.description")}
          </p>
        </div>

        {/* Vacancies grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {vacancies.map((vacancy) => (
            <Card
              key={vacancy.id}
              className="bg-card border-border hover:border-primary/50 transition-all flex flex-col"
            >
              <CardHeader>
                <CardTitle className="text-lg text-card-foreground">
                  {getTitle(vacancy)}
                </CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  {vacancy.location && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {vacancy.location}
                    </Badge>
                  )}
                  {vacancy.payRate && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {vacancy.payRate}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
                  {getDescription(vacancy)}
                </p>
                {getRequirements(vacancy) && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                      {t("vacancies.requirements")}
                    </p>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {getRequirements(vacancy)}
                    </p>
                  </div>
                )}
                <div className="mt-auto">
                  <Button
                    className="w-full group"
                    onClick={() => handleApply(vacancy.id)}
                  >
                    {t("vacancies.applyNow")}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}
